import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Tooltip,
  Skeleton,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CampaignIcon from '@mui/icons-material/Campaign';
import Chart from 'react-apexcharts';
import { toast } from 'react-toastify';
import MainCard from 'ui-component/cards/MainCard';
import { getApi, postApi } from 'views/services/api';

// ==============================|| PANEL DE MÉTRICAS SAAS ||============================== //

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount || 0);

const MetricTile = ({ label, value, caption, captionColor, tooltip, accent }) => {
  const theme = useTheme();
  const accentColor = accent || theme.palette.secondary.main;

  return (
    <Box
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
        borderLeft: `4px solid ${accentColor}`,
        backgroundColor: alpha(accentColor, 0.03)
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700 }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" sx={{ color: captionColor || 'text.secondary', display: 'flex', alignItems: 'center', mt: 0.25 }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
};

MetricTile.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  caption: PropTypes.node,
  captionColor: PropTypes.string,
  tooltip: PropTypes.string,
  accent: PropTypes.string
};

// Diálogo para registrar la inversión mensual en adquisición (base del CAC)
const SpendDialog = ({ open, onClose, onSaved }) => {
  const mesActual = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const [month, setMonth] = useState(mesActual);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [spends, setSpends] = useState([]);
  const [saving, setSaving] = useState(false);

  // Al abrir, traemos lo ya registrado para prellenar si el mes existe
  useEffect(() => {
    if (!open) return;
    const cargar = async () => {
      try {
        const respuesta = await getApi('api/acquisition-spend/list');
        setSpends(respuesta?.data?.spends || []);
      } catch (error) {
        console.error('Error al obtener inversiones registradas:', error);
      }
    };
    cargar();
  }, [open]);

  useEffect(() => {
    const existente = spends.find((s) => s.month === month);
    setAmount(existente ? String(existente.amount) : '');
    setNotes(existente ? existente.notes || '' : '');
  }, [month, spends]);

  const guardar = async () => {
    const value = Number(amount);
    if (!month || !Number.isFinite(value) || value < 0) {
      toast.error('Indica el mes y un monto válido');
      return;
    }
    try {
      setSaving(true);
      const respuesta = await postApi('api/acquisition-spend/upsert', { month, amount: value, notes });
      if (respuesta && respuesta.status === 200) {
        toast.success('Inversión registrada');
        onSaved?.();
        onClose();
      } else {
        toast.error(respuesta?.response?.data?.error || 'No se pudo registrar la inversión');
      }
    } catch (error) {
      toast.error('No se pudo registrar la inversión');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Inversión en adquisición</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Registra cuánto invertiste en marketing y ventas en el mes (publicidad, comisiones, campañas). Con esto se calcula el CAC.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Mes"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Monto invertido (USD)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            inputProps={{ min: 0, step: '0.01' }}
          />
          <TextField
            label="Notas (opcional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Ej: campaña Instagram + comisión vendedor"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" color="secondary" onClick={guardar} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SpendDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func
};

const SaasMetricsPanel = ({ isLoading, metricsData, onRefresh }) => {
  const theme = useTheme();
  const [openSpend, setOpenSpend] = useState(false);

  if (isLoading) {
    return (
      <MainCard title="Métricas del negocio (SaaS)">
        <Grid container spacing={2}>
          {[...Array(8)].map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rounded" height={90} />
            </Grid>
          ))}
        </Grid>
      </MainCard>
    );
  }

  const {
    mrr = 0,
    arr = 0,
    arpu = 0,
    churnRate = 0,
    activeSubscribersCount = 0,
    activeTrialsCount = 0,
    churnedCustomers = 0,
    churnedMRR = 0,
    ltv = null,
    avgLifetimeMonths = null,
    newCustomersThisMonth = 0,
    newMRRThisMonth = 0,
    trialsTotal = 0,
    trialsConverted = 0,
    trialConversionRate = 0,
    cashThisMonth = 0,
    cashLastMonth = 0,
    mrrHistory = [],
    mrrGrowthRate = null,
    cac = null,
    ltvCacRatio = null,
    cacPaybackMonths = null,
    spendThisMonth = 0,
    spendLast3Months = 0,
    newCustomersLast3Months = 0
  } = metricsData || {};

  const growthPositive = (mrrGrowthRate || 0) >= 0;
  const TrendIcon = growthPositive ? TrendingUpIcon : TrendingDownIcon;

  const mesCorto = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
  };

  const chartOptions = {
    chart: { toolbar: { show: false }, zoom: { enabled: false }, sparkline: { enabled: false } },
    stroke: { curve: 'smooth', width: 2 },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 90, 100] }
    },
    colors: [theme.palette.secondary.main],
    dataLabels: { enabled: false },
    xaxis: {
      categories: mrrHistory.map((h) => mesCorto(h.month)),
      labels: { style: { colors: theme.palette.text.secondary } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (val) => formatCurrency(val)
      }
    },
    grid: { borderColor: alpha(theme.palette.divider, 0.3), strokeDashArray: 4 },
    tooltip: { y: { formatter: (val) => formatCurrency(val) } }
  };

  const chartSeries = [{ name: 'MRR', data: mrrHistory.map((h) => h.mrr) }];

  return (
    <MainCard
      title="Métricas del negocio (SaaS)"
      secondary={
        <Button size="small" variant="outlined" color="secondary" startIcon={<CampaignIcon />} onClick={() => setOpenSpend(true)}>
          Registrar inversión
        </Button>
      }
    >
      <SpendDialog open={openSpend} onClose={() => setOpenSpend(false)} onSaved={onRefresh} />
      <Grid container spacing={2}>
        {/* --- Fila 1: ingresos --- */}
        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="MRR"
            value={formatCurrency(mrr)}
            tooltip="Monthly Recurring Revenue: ingreso mensual recurrente de todas las suscripciones de pago vigentes. Los planes anuales se prorratean a 12 meses."
            accent={theme.palette.secondary.main}
            caption={
              mrrGrowthRate !== null ? (
                <>
                  <TrendIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  {growthPositive ? '+' : ''}
                  {mrrGrowthRate}% vs mes anterior
                </>
              ) : (
                'Sin mes anterior para comparar'
              )
            }
            captionColor={mrrGrowthRate !== null ? (growthPositive ? theme.palette.success.main : theme.palette.error.main) : undefined}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="ARR"
            value={formatCurrency(arr)}
            caption="MRR × 12 (anualizado)"
            tooltip="Annual Recurring Revenue: proyección anual del ingreso recurrente actual."
            accent={theme.palette.primary.main}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="Cobrado este mes"
            value={formatCurrency(cashThisMonth)}
            caption={`Mes anterior: ${formatCurrency(cashLastMonth)}`}
            tooltip="Facturación real cobrada en el mes en curso (pagos registrados, sin prorratear). Distinto del MRR: aquí un plan anual cuenta completo el mes en que se cobró."
            accent={theme.palette.success.main}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="Nuevo MRR del mes"
            value={formatCurrency(newMRRThisMonth)}
            caption={`${newCustomersThisMonth} cliente${newCustomersThisMonth === 1 ? '' : 's'} nuevo${
              newCustomersThisMonth === 1 ? '' : 's'
            }`}
            tooltip="MRR aportado por hoteles que hicieron su PRIMER pago este mes. Mide la velocidad de adquisición."
            accent={theme.palette.info.main}
          />
        </Grid>

        {/* --- Fila 2: unit economics y retención --- */}
        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="ARPU"
            value={formatCurrency(arpu)}
            caption={`${activeSubscribersCount} hotel${activeSubscribersCount === 1 ? '' : 'es'} de pago`}
            tooltip="Average Revenue Per User: MRR dividido entre hoteles con suscripción de pago vigente. Los trials no cuentan."
            accent={theme.palette.info.dark}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="LTV"
            value={ltv !== null ? formatCurrency(ltv) : '—'}
            caption={avgLifetimeMonths !== null ? `Vida media: ${avgLifetimeMonths} meses` : 'Sin churn registrado aún'}
            tooltip="Lifetime Value: ingreso total esperado por cliente durante su vida como suscriptor (ARPU ÷ churn mensual). Con churn 0 no es calculable; es buena señal."
            accent={theme.palette.warning.dark}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="CAC"
            value={cac !== null ? formatCurrency(cac) : '—'}
            caption={
              cac !== null
                ? `LTV/CAC: ${ltvCacRatio !== null ? `${ltvCacRatio}×` : '—'} · Payback: ${
                    cacPaybackMonths !== null ? `${cacPaybackMonths} m` : '—'
                  }`
                : spendLast3Months > 0
                ? `Sin clientes nuevos en 3 meses (${newCustomersLast3Months})`
                : 'Registra tu inversión mensual'
            }
            tooltip="Costo de Adquisición de Cliente: inversión en marketing y ventas de los últimos 3 meses dividida entre los clientes nuevos del mismo período. Sano: LTV/CAC ≥ 3× y payback < 12 meses."
            accent={theme.palette.primary.dark}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="Churn (30 días)"
            value={`${churnRate}%`}
            caption={`${churnedCustomers} perdido${churnedCustomers === 1 ? '' : 's'} · ${formatCurrency(churnedMRR)} MRR`}
            tooltip="Tasa de cancelación: hoteles cuya suscripción venció en los últimos 30 días y no renovaron, sobre el total de clientes considerados. Incluye el MRR perdido (revenue churn)."
            accent={theme.palette.error.main}
            captionColor={churnedCustomers > 0 ? theme.palette.error.main : theme.palette.success.main}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="Trials"
            value={activeTrialsCount}
            caption={`Conversión: ${trialConversionRate}% (${trialsConverted}/${trialsTotal})`}
            tooltip="Hoteles en período de prueba gratis en este momento. La conversión mide cuántos hoteles que probaron terminaron pagando (histórico)."
            accent={theme.palette.warning.main}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <MetricTile
            label="Inversión (mes)"
            value={formatCurrency(spendThisMonth)}
            caption={`Últimos 3 meses: ${formatCurrency(spendLast3Months)}`}
            tooltip="Inversión en adquisición registrada para el mes en curso. Usa el botón «Registrar inversión» para capturarla o corregirla."
            accent={theme.palette.grey[600]}
          />
        </Grid>

        {/* --- Evolución del MRR --- */}
        {mrrHistory.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Evolución del MRR (últimos 6 meses)
            </Typography>
            <Chart options={chartOptions} series={chartSeries} type="area" height={220} />
          </Grid>
        )}
      </Grid>
    </MainCard>
  );
};

SaasMetricsPanel.propTypes = {
  isLoading: PropTypes.bool,
  metricsData: PropTypes.object,
  onRefresh: PropTypes.func
};

export default SaasMetricsPanel;
